import { AxiosApi, ApiResult } from './axiosApi';
import { ExportResult } from '../dtos/export';
import { FullSite, Site, SiteDto, SiteIssue } from '../dtos/site';
import { SiteAggregate } from '../dtos/siteAggregate';

export class SiteApi extends AxiosApi<Site> {
    constructor(baseUri: string, logout?: () => void, enqueueSnackbar?: any) {
        super(baseUri, 'sites', logout, enqueueSnackbar);
    }

    public getSitesAggregateForOrg = (orgId: number) =>
        this.genericRequest({
            method: 'GET',
            overridePath: `organizations/${orgId}/sites/aggregate`,
        }) as Promise<ApiResult<SiteAggregate[]>>;

    public getSitesAggregateForSite = (orgId: number, siteId: number) =>
        this.genericRequest({
            method: 'GET',
            overridePath: `organizations/${orgId}/sites/${siteId}/aggregate`,
        }) as Promise<ApiResult<SiteAggregate[]>>;

    public getForDealerAggregate = (dealerId: number) =>
        this.genericRequest({
            method: 'GET',
            overridePath: `dealers/${dealerId}/sites/aggregate`,
        });

    public manualOverride = (siteId: number, customerId: number, notes: string) =>
        this.genericRequest({
            method: 'POST',
            extraPath: `${siteId}/manualoverride/${customerId}`,
            data: { notes },
        }) as Promise<ApiResult<{ requestReference?: string; temporaryCode?: string; }>>;

    public endSession = (siteId: number) =>
        this.genericRequest({ method: 'PUT', extraPath: `${siteId}/endsession` }) as Promise<
            ApiResult<any>
        >;

    public post = (site: SiteDto) =>
        this.genericRequest({ method: 'POST', data: site }) as Promise<ApiResult<Site>>;

    public updateSite = (siteId: number, siteDto: SiteDto) =>
        this.genericRequest({
            method: 'PUT',
            extraPath: `${siteId}`,
            data: siteDto,
        }) as Promise<ApiResult<any>>;

    public getAllFull = () =>
        this.genericRequest({ method: 'GET', extraPath: `full` }) as Promise<ApiResult<FullSite[]>>;

    public getFullSiteById = (siteId: number) =>
        this.genericRequest({ method: 'GET', overridePath: `sites/${siteId}/full` }) as Promise<
            ApiResult<FullSite>
        >;

    public getForDealerFull = (dealerId: number) =>
        this.genericRequest({
            method: 'GET',
            overridePath: `dealers/${dealerId}/sites/full`,
        }) as Promise<ApiResult<FullSite[]>>;

    public getForOrgFull = (orgId: number) =>
        this.genericRequest({
            method: 'GET',
            overridePath: `organizations/${orgId}/sites/full`,
        }) as Promise<ApiResult<FullSite[]>>;

    public exportSitesForDealer = (dealerId: number) =>
        this.genericRequest({
            method: "GET",
            overridePath: `dealers/${dealerId}/sites/export`,
        }) as Promise<ApiResult<ExportResult>>;

    public exportSitesForOrg = (dealerId: number, orgId: number) =>
        this.genericRequest({
            method: "GET",
            overridePath: `dealers/${dealerId}/organizations/${orgId}/sites/export`,
        }) as Promise<ApiResult<ExportResult>>;

    public resetSensor = (siteId: number) =>
        this.genericRequest({
            method: 'POST',
            overridePath: `/Sites/${siteId}/sensorreset`,
        }) as Promise<ApiResult<unknown>>;

    public restartSystem = (siteId: number) =>
        this.genericRequest({
            method: 'POST',
            overridePath: `/Sites/${siteId}/systemrestart`,
        }) as Promise<ApiResult<unknown>>;

    public restartService = (siteId: number) =>
        this.genericRequest({
            method: 'POST',
            overridePath: `/Sites/${siteId}/servicerestart`,
        }) as Promise<ApiResult<unknown>>;

    public restartHmiDevice = (siteId: number) =>
        this.genericRequest({
            method: 'POST',
            overridePath: `/Sites/${siteId}/restarthmidevice`,
        }) as Promise<ApiResult<unknown>>;

    public restartBiometricDevice = (siteId: number) =>
        this.genericRequest({
            method: 'POST',
            overridePath: `/Sites/${siteId}/restartbiometricdevice`,
        }) as Promise<ApiResult<unknown>>;

    public activate = (siteId: number) =>
        this.genericRequest({
            method: 'PUT',
            extraPath: `${siteId}/activate`,
        }) as Promise<ApiResult<unknown>>;

    public deactivate = (siteId: number) =>
        this.genericRequest({
            method: 'PUT',
            extraPath: `${siteId}/deactivate`,
        }) as Promise<ApiResult<unknown>>;

    public updateSiteNotes = (
        request: { id: number, notes: string },
        abortController?: AbortController,
    ) =>
        this.genericRequest({
            method: 'PUT',
            data: { notes: request.notes },
            extraPath: `${request.id}/notes`,
            abortController,
        }) as Promise<ApiResult<unknown>>;

    public getIssues = (siteId: number) =>
        this.genericRequest({
            method: 'GET',
            extraPath: `${siteId}/issues`,
        }) as Promise<ApiResult<SiteIssue[]>>;
}
